import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import type { RevisionOverlayShape } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import { RevisionDiffOverlay } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionDiffOverlay'
import { revisionStatusLabel } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionStatusLegend'
import { useCursorAnchoredTooltip } from 'features/knowledgeDocumentViewer/components/revisionCompare/useCursorAnchoredTooltip'
import { useImageStatus } from 'features/knowledgeDocumentViewer/components/revisionCompare/useImageStatus'
import { useImageNaturalSize } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageNaturalSize'
import { applyCenteredScale } from 'features/knowledgeDocumentViewer/components/viewer/shared/applyCenteredScale'
import { useFitToViewport } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useFitToViewport'
import { frameRegionInViewer } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useZoomControls'
import { viewerZoomAnimationMs } from 'features/knowledgeDocumentViewer/components/viewer/shared/preferInstantViewerMotion'
import type { RegionFraming } from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import { Tooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import { transformWrapperMotionProps } from 'features/knowledgeDocumentViewer/components/viewer/shared/transformWrapperMotionProps'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { setFlowScale } from 'features/knowledgeDocumentViewer/graph/flowScale'
import type { RevisionDiffDetailBox } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { Maximize2, Minimize2 } from 'lucide-react'
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import styled from 'styled-components'

const Pane = styled.div`
  display: flex;
  min-height: 14rem;
  min-width: 0;
  flex-direction: column;
  gap: 0.375rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
`

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 0.0625rem solid var(--border);
  background-color: var(--card);
`

const PaneHeaderText = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
`

const PaneTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
`

const PaneSubtitle = styled.span`
  font-size: 0.75rem;
  color: var(--muted-foreground);
`

const PaneViewport = styled.div`
  position: relative;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  background-color: var(--background);
`

const TooltipStatus = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--muted-foreground);
`

const TooltipImpact = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--muted-foreground);
`

const TooltipHeadline = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
`

const TooltipLine = styled.div`
  max-width: 15rem;
  font-size: 0.75rem;
  color: var(--foreground);
`

const PaneImageWrapper = styled.div<{ $width: number; $height: number }>`
  position: relative;
  display: inline-block;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
`

const PaneImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
`

export const PaneEmptyState = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  font-size: 0.8125rem;
`

const EMPTY_IMAGE_SIZE = { w: 0, h: 0 }

// Tighter than the viewer's CONTEXT_FRAMING (0.3) so a clicked markup fills
// more of the pane; the min-region floor caps how far a tiny markup zooms in.
const REVISION_ZOOM_FRAMING: RegionFraming = {
  viewportFill: 0.45,
  minRegionPageFraction: 0.13,
}

export type RevisionComparePaneProps = {
  title: string
  subtitle: string
  imageUrl: string | undefined
  isLoadingImage: boolean
  shapes: RevisionOverlayShape[]
  selectedObjectIds?: ReadonlySet<string>
  selectedRegion?: RevisionDiffDetailBox | null
  showOverlay: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onShapeClick?: (objectId: string) => void
}

/** Imperative handle for resetting or targeting a pane's pan/zoom. */
export type RevisionComparePaneHandle = {
  resetView: () => void
  /** Animates to a normalized (0-1) box; `null` leaves the view unchanged. */
  zoomToBox: (box: RevisionDiffDetailBox | null) => void
}

export const RevisionComparePane = forwardRef<
  RevisionComparePaneHandle,
  RevisionComparePaneProps
>(function RevisionComparePane(
  {
    title,
    subtitle,
    imageUrl,
    isLoadingImage,
    shapes,
    selectedObjectIds,
    selectedRegion,
    showOverlay,
    isExpanded,
    onToggleExpand,
    onShapeClick,
  },
  ref
) {
  const { t } = useLingui()
  const imageNatural = useImageNaturalSize(imageUrl ?? null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const transformRef = useRef<ReactZoomPanPinchRef>(null)
  const overlaySvgRef = useRef<SVGSVGElement>(null)
  const [minScale, setMinScale] = useState(0.01)
  const { tooltip, layout, tooltipRef, onHover, onHoverEnd } =
    useCursorAnchoredTooltip(viewportRef)
  const imageStatus = useImageStatus(imageUrl ?? null)

  const { resetToFit, onFitComplete } = useFitToViewport(
    transformRef,
    setMinScale,
    imageNatural ?? EMPTY_IMAGE_SIZE,
    false,
    imageNatural !== null,
    8,
    true,
    null,
    imageUrl
  )

  // Seed --flow-image-unit from the current scale when the overlay first needs
  // it (toggled on, fit landed, or image resolved). onTransformed only fires on
  // a user gesture, so without this the overlay renders at the fallback 1px unit
  // — faint — until the first zoom.
  useLayoutEffect(() => {
    const scale = transformRef.current?.instance.transformState.scale
    if (scale) setFlowScale(overlaySvgRef.current, scale)
  }, [showOverlay, onFitComplete, imageNatural])

  useImperativeHandle(
    ref,
    () => ({
      resetView: resetToFit,
      zoomToBox: (box) => {
        if (!transformRef.current || !box) return
        const bbox: NormalizedBBox = {
          left: box.x0,
          top: box.y0,
          width: box.x1 - box.x0,
          height: box.y1 - box.y0,
        }
        frameRegionInViewer(
          transformRef.current,
          bbox,
          minScale,
          viewerZoomAnimationMs(),
          REVISION_ZOOM_FRAMING
        )
      },
    }),
    [minScale, resetToFit]
  )

  return (
    <Pane>
      <PaneHeader>
        <PaneHeaderText>
          <PaneTitle>{title}</PaneTitle>
          <PaneSubtitle>{subtitle}</PaneSubtitle>
        </PaneHeaderText>
        <Button
          variant='ghost'
          size='icon-sm'
          aria-label={isExpanded ? t`Restore` : t`Maximize`}
          onClick={onToggleExpand}>
          {isExpanded ? (
            <Minimize2 size={14} strokeWidth={1.5} />
          ) : (
            <Maximize2 size={14} strokeWidth={1.5} />
          )}
        </Button>
      </PaneHeader>
      <PaneViewport ref={viewportRef}>
        {imageUrl && imageStatus !== 'error' ? (
          <ZoomControls
            onZoomIn={() =>
              applyCenteredScale(transformRef.current, (scale) =>
                Math.min(scale * 2, 10)
              )
            }
            onZoomOut={() =>
              applyCenteredScale(transformRef.current, (scale) =>
                Math.max(scale / 2, minScale)
              )
            }
            onResetZoom={resetToFit}
          />
        ) : null}
        {imageUrl && imageStatus !== 'error' ? (
          <TransformWrapper
            ref={transformRef}
            minScale={minScale}
            maxScale={10}
            limitToBounds
            onTransformed={(pinchRef: ReactZoomPanPinchRef) =>
              setFlowScale(
                overlaySvgRef.current,
                pinchRef.instance.transformState.scale
              )
            }
            centerOnInit={false}
            wheel={{ step: 0.5 }}
            {...transformWrapperMotionProps()}>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{
                display: 'inline-block',
                opacity: onFitComplete ? 1 : 0,
              }}>
              {imageNatural ? (
                <PaneImageWrapper
                  $width={imageNatural.w}
                  $height={imageNatural.h}>
                  <PaneImage src={imageUrl} alt={title} draggable={false} />
                  {showOverlay ? (
                    <RevisionDiffOverlay
                      ref={overlaySvgRef}
                      shapes={shapes}
                      selectedObjectIds={selectedObjectIds}
                      selectedRegion={selectedRegion}
                      imageNatural={imageNatural}
                      onShapeHover={onHover}
                      onShapeHoverEnd={onHoverEnd}
                      onShapeClick={onShapeClick}
                    />
                  ) : null}
                </PaneImageWrapper>
              ) : null}
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <PaneEmptyState>
            {imageStatus === 'error' || !isLoadingImage ? (
              <Trans>No image available</Trans>
            ) : (
              <Trans>Loading page…</Trans>
            )}
          </PaneEmptyState>
        )}
        {tooltip && layout ? (
          <Tooltip
            ref={tooltipRef}
            $left={layout.leftPos}
            $top={layout.topPos}
            $transform={
              layout.renderBelow
                ? 'translate(-50%, 0)'
                : 'translate(-50%, -100%)'
            }>
            <TooltipStatus>
              {revisionStatusLabel(tooltip.content.status)}
            </TooltipStatus>
            {tooltip.content.impact ? (
              <TooltipImpact>
                <Trans>Impact:</Trans> {tooltip.content.impact}
              </TooltipImpact>
            ) : null}
            {tooltip.content.title || tooltip.content.cls ? (
              <TooltipLine>
                {tooltip.content.title || tooltip.content.cls}
              </TooltipLine>
            ) : null}
            {tooltip.content.headline ? (
              <TooltipHeadline>{tooltip.content.headline}</TooltipHeadline>
            ) : null}
            {tooltip.content.details ? (
              <TooltipLine>{tooltip.content.details}</TooltipLine>
            ) : null}
            {tooltip.content.oldText || tooltip.content.newText ? (
              <TooltipLine>
                {tooltip.content.oldText ?? '—'} →{' '}
                {tooltip.content.newText ?? '—'}
              </TooltipLine>
            ) : null}
          </Tooltip>
        ) : null}
      </PaneViewport>
    </Pane>
  )
})
